import { useEditModalStore } from '../../../model/use-edit-modal.store';
import styles from './styles.module.scss';
import { WarrantyClaimNoteStatusEnum, WarrantyLogNoteDto, WarrantyLogNoteType } from 'api-new/generated';
import clsx from 'clsx';
import { InlineSvg } from 'components/svg';
import { useWarrantyLogAddNoteMutation } from 'entities/warranty';
import { useAuth } from 'features/auth/contexts/auth';
import { formatErrorMessage, QueryError } from 'features/shared/model';
import moment from 'moment';
import { FC, memo, useEffect, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { toast } from 'react-toastify';
import { Button, SkeletonWrapper } from 'shared';
import { formatDateSafe } from 'utils/formats';

const SCROLLBAR_HEIGHT = '400px';
type Props = {
  status?: WarrantyClaimNoteStatusEnum;
  id?: number;
  noteType: WarrantyLogNoteType;
  notes?: WarrantyLogNoteDto[] | null;
};

export const Notes: FC<Props> = memo(function Notes({ status, id, noteType, notes }: Props) {
  const { currentUser } = useAuth();
  const [note, setNote] = useState<string>();
  const [localNotes, setLocalNotes] = useState<WarrantyLogNoteDto[] | null>();

  useEffect(() => {
    setLocalNotes(notes?.length ? [...notes] : []);
  }, [notes]);

  const { mutate: addNote, isLoading: isLoadingAddNote } = useWarrantyLogAddNoteMutation();

  const isLoading = useEditModalStore((s) => s.isLoading);

  const handleAddNote = () => {
    if (!id) return;

    const text = note?.trim();

    if (!text?.length) return;

    const requestBody = {
      logType: noteType,
      text: text,
      status: status
    };

    addNote(
      {
        logId: id!,
        requestBody: { ...requestBody }
      },
      {
        onSuccess: (data) => {
          toast.success('Successfully create note!');
          setNote('');
          const notes = localNotes ?? [];
          setLocalNotes([
            {
              ...requestBody,
              id: data,
              userId: currentUser?.userId,
              userName: currentUser?.userName,
              created: moment().format('YYYY-MM-DD[T]HH:mm:ss')
            },
            ...notes
          ]);
        },
        onError: (error: QueryError) => {
          const message = formatErrorMessage(error);
          toast.error(message);
          console.error(message);
        }
      }
    );
  };

  return (
    <div className={styles.container}>
      <SkeletonWrapper isShow={isLoading} height={200}>
        <div className={styles.header}>
          <div className={styles.title}>{noteType == WarrantyLogNoteType.Note ? 'Notes' : 'WA Notes'}</div>
          <button type="button" className={styles.sendBtn}>
            <InlineSvg path="/media/icons/custom/notify1.svg" />
            Send 🠅
          </button>
        </div>
        <div className="mt-3 mb-6">
          <textarea
            className="form-control form-control-solid border-0 pe-10 resize-none mb-3"
            rows={4}
            disabled={isLoadingAddNote}
            value={note ?? ''}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            text="Add Note"
            variant="secondary"
            size="sm"
            icon={<InlineSvg path="/media/icons/custom/send.svg" />}
            isLoading={isLoadingAddNote}
            disabled={Boolean(note?.length) == false}
            onClick={handleAddNote}
          />
        </div>
      </SkeletonWrapper>
      <div className={styles.separation}></div>

      <SkeletonWrapper isShow={isLoading} height={200}>
        <PerfectScrollbar
          style={{ height: SCROLLBAR_HEIGHT, paddingRight: '10px', marginBottom: '10px', width: '100%' }}
        >
          <div className={clsx(styles.noteContainer)}>
            {localNotes?.map((x) => (
              <div key={x.id} className={styles.noteItem}>
                <div className="d-flex gap-3 flex-nowrap mb-2">
                  <div className={styles.name}>{x.userName ?? 'n/a'}</div>
                  <div className={styles.time}>{formatDateSafe(x.created, 'DD/MM/YYYY HH:mm', 'n/a')}</div>
                </div>
                <div>{x.text}</div>
              </div>
            ))}
          </div>
        </PerfectScrollbar>
      </SkeletonWrapper>
    </div>
  );
});
